/**
 * Arayüz dili altyapısı (M6 WP 6.2 "dil paketleri"). Kapsamlı bir i18n kitaplığı kurulmaz (WP 2.7 K-8):
 *
 * - Kaynak sözlük Türkçedir (`tr.ts`) ve TAMDIR. Başka bir dil, kaynak sözlüğün KISMİ bir örtüsüdür (`en.ts`); örtüde
 *   olmayan, boş ya da türü uyuşmayan her anahtar Türkçeye düşer (`mergeDictionary`).
 * - Etkin dil çalışma alanı ayarıdır (`tenant_settings.language`). Sözlük modül yüklenirken çözülür — bazı metinler modül
 *   düzeyinde sabitlere alınır (ör. menü tanımı) — bu yüzden dil tarayıcıda küçük bir tercih anahtarında tutulur ve kabuk,
 *   sunucudaki ayar farklıysa tercihi güncelleyip sayfayı BİR KEZ yeniden yükler (`syncUiLanguage`).
 * - Saklanan değer yalnız dil kodudur (kişisel veri ya da sunucu yanıtı değil; shell/preferences.ts kuralı).
 */
import { UI_LANGUAGES, type UiLanguage } from '@castinflow/shared';

const KEY = 'cf.ui.language';

const isUiLanguage = (v: unknown): v is UiLanguage =>
  typeof v === 'string' && (UI_LANGUAGES as readonly string[]).includes(v);

/** Tarayıcıda saklanan arayüz dili; depolama yoksa/okunamıyorsa ya da değer tanımsızsa Türkçe. */
export function storedUiLanguage(): UiLanguage {
  try {
    // Depolama yoksa (tarayıcı dışı bağlam) erişim hata fırlatır → Türkçe
    const v = globalThis.localStorage.getItem(KEY);
    return isUiLanguage(v) ? v : 'tr';
  } catch {
    return 'tr';
  }
}

/** Tercihi yazar; yazılamadıysa (gizli pencere, politika) `false`. */
export function storeUiLanguage(language: UiLanguage): boolean {
  try {
    globalThis.localStorage.setItem(KEY, language);
    return storedUiLanguage() === language;
  } catch {
    return false;
  }
}

/**
 * Sunucudaki çalışma alanı dili ile tarayıcıdaki tercih farklıysa tercihi günceller ve yeniden yükleme gerekip gerekmediğini
 * döndürür. Tercih yazılamıyorsa `false` (yeniden yükleme döngüsü olmaz; oturum Türkçe sürer).
 */
export function syncUiLanguage(serverLanguage: UiLanguage): boolean {
  if (storedUiLanguage() === serverLanguage) return false;
  return storeUiLanguage(serverLanguage);
}

/** Sözlük örtüsü: aynı biçimin kısmi hâli (metinler `string`, fonksiyonlar aynı imza). */
export type DictionaryOverlay<T> = {
  [K in keyof T]?: T[K] extends (...args: infer A) => string
    ? (...args: A) => string
    : T[K] extends string
      ? string
      : T[K] extends object
        ? DictionaryOverlay<T[K]>
        : never;
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Kaynak sözlüğün biçimini koruyarak örtüyü uygular. Örtüdeki değer yalnız türü kaynakla aynıysa ve (metinse) boş değilse
 * alınır; aksi hâlde kaynak (Türkçe) değer kalır. Kaynakta olmayan anahtar yok sayılır.
 */
export function mergeDictionary<T extends object>(base: T, overlay: unknown): T {
  if (!isPlainObject(overlay)) return base;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(base as Record<string, unknown>)) {
    const o = overlay[key];
    if (isPlainObject(value)) out[key] = mergeDictionary(value, o);
    else if (typeof value === 'string')
      out[key] = typeof o === 'string' && o.trim() !== '' ? o : value;
    else if (typeof value === 'function') out[key] = typeof o === 'function' ? o : value;
    else out[key] = value;
  }
  return out as T;
}

/** Sözlükteki yaprak (metin/fonksiyon) sayısı ve örtünün bunlardan kaçını çevirdiği. */
export function dictionaryCoverage(
  base: object,
  overlay: unknown,
): { translated: number; total: number } {
  let translated = 0;
  let total = 0;
  const walk = (b: unknown, o: unknown) => {
    if (isPlainObject(b)) {
      for (const [k, v] of Object.entries(b)) walk(v, isPlainObject(o) ? o[k] : undefined);
      return;
    }
    if (typeof b !== 'string' && typeof b !== 'function') return;
    total += 1;
    if (typeof b === 'string' ? typeof o === 'string' && o.trim() !== '' : typeof o === 'function')
      translated += 1;
  };
  walk(base, overlay);
  return { translated, total };
}
